export interface DomainIdsConfig {
  OPEN_ID_BASE_URL: string
  AUTH_CLIENT_ID: string
  LOGIN_SCOPES: string[]
  USE_SSO_IDP?: string
}

export interface Configuration {
  AUTH: Auth
  API_URL: string
  OPEN_ID_BASE_URL: string
  ENVIRONMENT: string
  GTM_TAG_ID: string
  IS_WINDOW_ENV_CONFIG?: boolean | undefined
  AUTH_CLIENT_ID: string,
  APP_INSIGHTS_CONNECTION_STRING: string,
  LOGIN_SCOPES: string[]
  USE_SSO_IDP?: string
  FEATURE_SERVICE_ALERTS: boolean
}

export interface Auth {
  baseUrl: string
  watchLoggedIn: boolean
  redirect: Redirect
  strategies: Strategies
}

export interface Redirect {
  home: string
  callback: string
  logout: string
  login: string
  postLogout: string
}

export interface Strategies {
  oidc: Oidc
}

export interface Oidc {
  scheme: string
  endpoints: Endpoints
  clientId: string
}

export interface Endpoints {
  configuration: string
}

declare global {
  interface Window { ENV: any | undefined; }
}

/**
 * This function will try to create a `Configuration object` from global variable `window`.
 * It will do some basic validation for `null/undefined` of the variables. <br>
 * More documentation about configuration: 
 * https://educationgroup.visualstudio.com/HQ/_wiki/wikis/CloudOps/2021/ver.-5.8-Frontend-CD-component?anchor=how-to-do-configuration
 */
export function useConfiguration(): Configuration {

  if (window.ENV == undefined) throw new Error("Cannot use window.ENV configuration because it is null.")
  if (window.ENV.API_URL == undefined) throw new Error("window.ENV.API_URL configuration is null or undefined.")
  if (window.ENV.ENVIRONMENT == undefined) throw new Error("window.ENV.ENVIRONMENT configuration is null or undefined.")
  if (window.ENV.GTM_TAG_ID == undefined) throw new Error("window.ENV.GTM_TAG_ID configuration is null or undefined.")
  if (window.ENV.DOMAIN_IDS_MAP != undefined) {
    for (const [domain, config] of Object.entries(window.ENV.DOMAIN_IDS_MAP)) {
      if ((config as DomainIdsConfig).OPEN_ID_BASE_URL == undefined)
        throw new Error(`window.ENV.DOMAIN_IDS_MAP["${domain}"].OPEN_ID_BASE_URL is null or undefined.`)
      if ((config as DomainIdsConfig).AUTH_CLIENT_ID == undefined)
        throw new Error(`window.ENV.DOMAIN_IDS_MAP["${domain}"].AUTH_CLIENT_ID is null or undefined.`)
      if ((config as DomainIdsConfig).LOGIN_SCOPES == undefined)
        throw new Error(`window.ENV.DOMAIN_IDS_MAP["${domain}"].LOGIN_SCOPES is null or undefined.`)
    }
  }
  if (window.ENV.APP_INSIGHTS_CONNECTION_STRING == undefined) throw new Error("window.ENV.APP_INSIGHTS_CONNECTION_STRING configuration is null or undefined.")

  const hostname = window.location.hostname;
  const domainOverride: DomainIdsConfig = window.ENV.DOMAIN_IDS_MAP[hostname];

  const OPEN_ID_BASE_URL = domainOverride.OPEN_ID_BASE_URL;
  const AUTH_CLIENT_ID = domainOverride.AUTH_CLIENT_ID;

  const AUTH: Auth = {
    baseUrl: OPEN_ID_BASE_URL,
    watchLoggedIn: false,
    redirect: {
      home: '/',
      callback: '/callback',
      logout: '',
      login: '',
      postLogout: '/'
    },
    strategies: {
      oidc: {
        scheme: 'openIDConnect',
        endpoints: {
          configuration: `${OPEN_ID_BASE_URL}/.well-known/openid-configuration`,
        },
        clientId: AUTH_CLIENT_ID,
      },
    },
  }

  return {
    API_URL: window.ENV.API_URL,
    ENVIRONMENT: window.ENV.ENVIRONMENT,
    GTM_TAG_ID: window.ENV.GTM_TAG_ID,
    AUTH,
    AUTH_CLIENT_ID,
    OPEN_ID_BASE_URL,
    APP_INSIGHTS_CONNECTION_STRING: window.ENV.APP_INSIGHTS_CONNECTION_STRING,
    LOGIN_SCOPES: domainOverride.LOGIN_SCOPES,
    USE_SSO_IDP: domainOverride.USE_SSO_IDP,
    // Optional feature flag: anything other than `true` keeps the feature disabled.
    FEATURE_SERVICE_ALERTS: window.ENV.FEATURE_SERVICE_ALERTS === true
  }
}